import type { ReorderLine, UnavailableReason } from '@commerce/core'

/**
 * `1 artículo`, `3 artículos` — the cart count as prose.
 *
 * Shared because the count is spoken in three places (header badge, bottom
 * nav, "Realizar pedido" bar) and a singular that only one of them gets right
 * is worse than one none of them get right.
 */
export const articlesText = (count: number): string =>
  count === 1 ? '1 artículo' : `${count} artículos`

/**
 * Why a line of a repeated order cannot come back, in the shopper's words.
 *
 * The two reasons are kept apart because they ask for different things: "sin
 * stock" is a wait, and a delisted product is not coming back at all. One
 * merged "no disponible" would send someone back tomorrow for nothing.
 */
const UNAVAILABLE_REASON: Record<UnavailableReason, string> = {
  outOfStock: 'sin stock',
  notInCatalog: 'ya no está en el catálogo',
}

/**
 * "Coca 600ml (sin stock), Galletas (ya no está en el catálogo)".
 *
 * Shared because both ways into "repeat this order" — the order detail screen
 * and the home card — have to name the same lines with the same reasons, and a
 * shopper who reads two different sentences about one cart is being told the
 * screens disagree.
 */
export const unavailableLinesText = (
  lines: readonly (ReorderLine & { availability: UnavailableReason })[],
): string =>
  lines.map((line) => `${line.label} (${UNAVAILABLE_REASON[line.availability]})`).join(', ')
